'use client';

/**
 * Mint a visitor account ON THE GESTURE THAT NEEDS ONE, without navigating.
 *
 * WHY (2026-09-20, the Revent first-touch runs found `/w/` silently dropping
 * every write, and the first fix only moved the drop):
 *
 * `host.kv` became per-viewer and content-addressed, so a signed-out visitor
 * opening a share link CAN have a store — they just need an identity to own
 * it. `/w/` already mints one (`ViewerGuestMint`), but only when the mount
 * needs a session to render at all: the page passes `collabContent` from
 * `handler.spec.collab` and `codeEnrolls` from whether a share code confers
 * anything. timer, todo, snake, invaders and blocks declare neither, so they
 * mounted with no session, every `host.kv.set` came back 401, and the apps
 * call it without awaiting or catching — so it became an unhandled rejection
 * and the person saw an edit accepted and then gone on reload. The exact
 * failure the rework existed to remove.
 *
 * THE NARROWING ON THAT MOUNT IS RIGHT AND IS LEFT ALONE. `/w/` is crawlable
 * and it is the only indexable page that mints; widening it to every app mount
 * would put a database row and a bot challenge behind every link preview and
 * every idle look at a shared app. `ViewerGuestMint`'s own docblock says there
 * are exactly two ways for the mint to be load-bearing and both are the
 * caller's to answer. This is the third, and it is answered by the WRITE
 * rather than by the mount: nobody who only looks at a shared app becomes an
 * account, and anybody whose app tries to save becomes one immediately.
 *
 * Deliberately NOT `makeFetchWithGuestMint`, which mints and then FULL
 * NAVIGATES (a workspace clone, or a reload). Reloading `/w/` out from under
 * someone because their todo autosaved is its own data loss. `startGuest` sets
 * the session cookie in place, so the next same-origin request carries it.
 *
 * A module singleton, like `guest-mint-gate`: the caller is the widget bridge,
 * and several apps (or several keys in one app) racing their first save must
 * produce ONE account, not one per write.
 */

import { startGuest } from '@/lib/auth/guest-client';
import { executeTurnstile } from '@/lib/auth/turnstile-execute';
import { markGuestMintPending, settleGuestMint } from '@/lib/auth/guest-mint-gate';

/**
 * How long this path waits for Cloudflare, well under the module default of
 * 60s and matching `makeFetchWithGuestMint` for the same reason: the person is
 * mid-edit with nothing on screen explaining the wait, and a challenge that is
 * never answered must not park their save forever. A silent verification
 * returns in well under a second.
 */
const TURNSTILE_TIMEOUT_MS = 15_000;

/** One own-attempt per page. `null` = never tried. */
let outcome: boolean | null = null;
let inFlight: Promise<boolean> | null = null;

/**
 * Ensure a session exists, minting a workspace-less visitor account if there
 * is none. Resolves true when the caller should RETRY its request.
 *
 * Callers should wait on an already-announced mint (`awaitGuestMint`) BEFORE
 * reaching here: that 401 is a boot race rather than an answer, and minting
 * over it would make two accounts for one visitor.
 *
 * Never throws. A refused mint (no Turnstile token, rate limited) resolves
 * false, and the caller's job is then to report an honest failure rather than
 * to pretend the write landed.
 */
export function ensureGuestSession(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  // One attempt per page. A second challenge for a visitor we already failed
  // to mint buys nothing and would run the bot check on every keystroke's
  // autosave.
  if (outcome !== null) return Promise.resolve(outcome);
  if (inFlight) return inFlight;
  inFlight = (async () => {
    // Announce it so anything else on the critical path (the collab token)
    // waits for the session instead of racing it into a fatal 401.
    markGuestMintPending();
    try {
      // A null token is fine to send: the server allows it wherever no secret
      // is configured, which is exactly where the challenge could not run.
      const turnstileToken = await executeTurnstile(TURNSTILE_TIMEOUT_MS).catch(() => null);
      const res = await startGuest({ turnstileToken });
      outcome = res.ok;
    } catch {
      outcome = false;
    } finally {
      settleGuestMint();
      inFlight = null;
    }
    return outcome ?? false;
  })();
  return inFlight;
}

/** Test seam. Resets the once-per-page memo. */
export function resetGuestMintOnDemandForTests(): void {
  outcome = null;
  inFlight = null;
}
